import { getJson, sendJson } from 'src/components/star-grid/services/starGridHttp';
import { DATASETS_PREFIX } from 'src/components/star-grid/starGridConstants';
import type {
  AccessCheckResponse,
  Dataset,
  DatasetColumnsResponse,
  DatasetDescriptionInput,
  DatasetFixedFilter,
  DatasetLookup,
  DatasetPreviewRequest,
  DatasetPreviewResponse,
  DatasetProfile,
  DatasetSourcesResponse,
  HierarchyPairsResponse,
  LookupCandidatesResponse,
  LookupCheckResult,
  ScenarioSuggestionsResponse
} from 'src/components/star-grid/datasets/datasetTypes';

const datasetPath = (datasetId: string, suffix = ''): string => `${DATASETS_PREFIX}/${encodeURIComponent(datasetId)}${suffix}`;

/** `role_arn` for a request body, only when a role is given: omitted, the service reads with its own role. */
const withRoleArn = (roleArn?: string): { role_arn?: string } => (roleArn ? { role_arn: roleArn } : {});

/** The whole approved-table catalog (server-cached, system DBs excluded). `refresh` forces a cache rebuild. */
export function getDatasetSources(search?: string, refresh?: boolean, signal?: AbortSignal): Promise<DatasetSourcesResponse> {
  const params: string[] = [];
  if (search) params.push(`search=${encodeURIComponent(search)}`);
  if (refresh) params.push('refresh=true');
  const query = params.length ? `?${params.join('&')}` : '';
  return getJson<DatasetSourcesResponse>(`${DATASETS_PREFIX}/sources${query}`, signal);
}

/** Every `(dimension, hierarchy)` pair, for picking a hierarchy the profiler did not suggest. */
export function listHierarchies(signal?: AbortSignal): Promise<HierarchyPairsResponse> {
  return getJson<HierarchyPairsResponse>(`${DATASETS_PREFIX}/hierarchies`, signal);
}

/** The columns of a lookup table, for choosing the join key and the attributes to pull across. */
export function getDatasetColumns(ref: string, signal?: AbortSignal, roleArn?: string): Promise<DatasetColumnsResponse> {
  const query = new URLSearchParams({ ref, ...withRoleArn(roleArn) });
  return getJson<DatasetColumnsResponse>(`${DATASETS_PREFIX}/columns?${query}`, signal);
}

/** Profiler-proposed lookup joins for the given dimension columns, each pre-scored with its checks. */
export function suggestLookupCandidates(
  ref: string,
  columns: readonly string[],
  signal?: AbortSignal,
  fixedFilters: readonly DatasetFixedFilter[] = [],
  roleArn?: string
): Promise<LookupCandidatesResponse> {
  return sendJson<LookupCandidatesResponse>(
    'POST',
    `${DATASETS_PREFIX}/lookup-candidates`,
    { ref, columns: [...columns], fixed_filters: [...fixedFilters], ...withRoleArn(roleArn) },
    signal
  );
}

/** Validates one lookup join against the fact table: key uniqueness, match rate, and row-count impact. */
export function checkLookup(
  ref: string,
  lookup: DatasetLookup,
  signal?: AbortSignal,
  fixedFilters: readonly DatasetFixedFilter[] = [],
  roleArn?: string
): Promise<LookupCheckResult> {
  return sendJson<LookupCheckResult>(
    'POST',
    `${DATASETS_PREFIX}/lookup-check`,
    { ref, lookup, fixed_filters: [...fixedFilters], ...withRoleArn(roleArn) },
    signal
  );
}

/** Confirms the service role can read the table. A blocked result is a 200 body, not an error. */
export function checkDatasetAccess(ref: string, signal?: AbortSignal, roleArn?: string): Promise<AccessCheckResponse> {
  return sendJson<AccessCheckResponse>('POST', `${DATASETS_PREFIX}/access-check`, { ref, ...withRoleArn(roleArn) }, signal);
}

/** Columns, types, samples, row count, and the profiler's role/hierarchy suggestions, within the fixed filters. */
export function profileDataset(
  ref: string,
  signal?: AbortSignal,
  fixedFilters: readonly DatasetFixedFilter[] = [],
  roleArn?: string
): Promise<DatasetProfile> {
  return sendJson<DatasetProfile>('POST', `${DATASETS_PREFIX}/profile`, { ref, fixed_filters: [...fixedFilters], ...withRoleArn(roleArn) }, signal);
}

/** Every value of the scenario column and the rule-based scenario map over them, each with its reason. */
export function suggestScenarioMap(
  ref: string,
  column: string,
  time: { readonly column?: string; readonly format?: string },
  signal?: AbortSignal,
  fixedFilters: readonly DatasetFixedFilter[] = [],
  roleArn?: string
): Promise<ScenarioSuggestionsResponse> {
  return sendJson<ScenarioSuggestionsResponse>(
    'POST',
    `${DATASETS_PREFIX}/scenario-suggestions`,
    { ref, column, time_column: time.column, time_format: time.format, fixed_filters: [...fixedFilters], ...withRoleArn(roleArn) },
    signal
  );
}

/** Every onboarded dataset; `forReports` asks for only those a report may be built on (published, global). */
export async function listDatasets(signal?: AbortSignal, forReports = false): Promise<Dataset[]> {
  // The route wraps the list (`{ datasets: [...] }`), matching `/sources`.
  const response = await getJson<{ readonly datasets?: readonly Dataset[] }>(
    forReports ? `${DATASETS_PREFIX}?for_reports=true` : DATASETS_PREFIX,
    signal
  );
  return [...(response.datasets ?? [])];
}

/** One dataset's full description, the entry point for building a report on it. A pinned `version` is optional. */
export function getDataset(datasetId: string, version?: number, signal?: AbortSignal): Promise<Dataset> {
  const query = version === undefined ? '' : `?version=${encodeURIComponent(version)}`;
  return getJson<Dataset>(datasetPath(datasetId, query), signal);
}

/** Creates a draft dataset. Throws on the server's fan-out refusal (differing row counts). */
export function createDataset(description: DatasetDescriptionInput, signal?: AbortSignal): Promise<Dataset> {
  return sendJson<Dataset>('POST', DATASETS_PREFIX, description, signal);
}

/** Saves an edit to an existing dataset as a new version. Owner-only, enforced server-side. */
export function updateDataset(datasetId: string, description: DatasetDescriptionInput, signal?: AbortSignal): Promise<Dataset> {
  return sendJson<Dataset>('PUT', datasetPath(datasetId), description, signal);
}

/** Publishes a draft dataset. Owner-only, enforced server-side. */
export function publishDataset(datasetId: string, signal?: AbortSignal): Promise<Dataset> {
  return sendJson<Dataset>('POST', datasetPath(datasetId, '/publish'), {}, signal);
}

/** The query an unsaved description would run, generated without running it. */
export function previewDraftDataset(
  description: DatasetDescriptionInput,
  request: DatasetPreviewRequest,
  signal?: AbortSignal
): Promise<DatasetPreviewResponse> {
  return sendJson<DatasetPreviewResponse>('POST', `${DATASETS_PREFIX}/draft-preview`, { dataset: description, preview: request, run: false }, signal);
}

/** Reporting scenario names a deck blend reads: the keys of a dataset's scenario map. */
export async function getReportingScenarios(signal?: AbortSignal): Promise<string[]> {
  const response = await getJson<{ readonly scenarios?: readonly string[] }>(`${DATASETS_PREFIX}/reporting-scenarios`, signal);
  return [...(response.scenarios ?? [])];
}
